// Outbound half of the cross-domain SSO hand-off (hub: trackingmcp.com/auth/hand-off).
//
// A customer signed in on the hub should not log in a second time here. On the FIRST signed-out load of
// a tab we send the page once to the hub, which (if it holds a session) comes back to us with a
// single-use nonce in the URL fragment (`#sso=`); adopt-session.ts redeems it. If the hub has no
// session, or the visitor cancels, it returns with no nonce and we show our own login.
//
// Rules (docs: hub repo, sso-hand-off-spoke-contract.md):
//  - never loop: the marker is written BEFORE the redirect and one attempt is all a tab gets;
//  - the return URL carries no token and no fragment, only origin + path + query;
//  - the nonce is never read, logged or forwarded here (adopt-session.ts owns it).

export const HUB_HAND_OFF_URL = 'https://trackingmcp.com/auth/hand-off'
export const SSO_TRIED_KEY = 'sso_tried'

// Hosts the hub allowlist accepts as `return` for this site. Anything else (localhost, previews,
// staging) is never redirected: the hub would refuse it anyway.
const SPOKE_HOSTS: ReadonlySet<string> = new Set(['schedulesmcp.com', 'www.schedulesmcp.com'])

// The redirect belongs to the places where a signed-in state matters. Every other route is public and
// indexable (home, lanes, carriers, ports, vessels, docs, pricing...): a first-time visitor there must see
// THIS site, never the hub's login page. A visitor who reaches /login or /dashboard still gets the hop.
const SIGN_IN_ROUTES: ReadonlySet<string> = new Set(['/login', '/dashboard', '/admin'])
export const isSignInRoute = (pathname: string) =>
  SIGN_IN_ROUTES.has(pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname)

const BOT_UA =
  /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|embedly|quora link|pinterest|whatsapp|telegram|headless|lighthouse|prerender|python-requests|curl\/|wget\//i

// Query/hash keys that mean "an auth flow is in progress on this URL": never redirect, never carry.
const AUTH_KEYS = /(^|[?&#])(access_token|refresh_token|token_hash|code|type|error|error_code)=/i

export type SsoEnv = {
  hasSession: boolean
  href: string // current page URL
  userAgent: string
  webdriver: boolean
  embedded: boolean // inside an iframe / federated remote
  markerSet: boolean
  markerWritable: boolean
}

export type SsoDecision = { redirect: false; reason: string } | { redirect: true; url: string }

/** Pure: should this load go to the hub, and where. */
export function decideSsoRedirect(env: SsoEnv): SsoDecision {
  if (env.hasSession) return { redirect: false, reason: 'has-session' }
  if (env.markerSet) return { redirect: false, reason: 'already-tried' }
  // Fail closed: if we cannot persist the marker we cannot guarantee no loop.
  if (!env.markerWritable) return { redirect: false, reason: 'no-storage' }
  if (env.embedded) return { redirect: false, reason: 'embedded' }
  if (env.webdriver || BOT_UA.test(env.userAgent)) return { redirect: false, reason: 'bot' }

  let u: URL
  try {
    u = new URL(env.href)
  } catch {
    return { redirect: false, reason: 'bad-url' }
  }
  if (u.protocol !== 'https:' || u.port || u.username || u.password)
    return { redirect: false, reason: 'not-spoke-origin' }
  if (!SPOKE_HOSTS.has(u.hostname)) return { redirect: false, reason: 'not-spoke-origin' }
  if (!isSignInRoute(u.pathname)) return { redirect: false, reason: 'public-route' }
  // A handed-back nonce, or any auth callback payload, is being handled by someone else.
  if (/(^|[#&])sso=/.test(u.hash) || AUTH_KEYS.test(u.hash) || AUTH_KEYS.test(u.search))
    return { redirect: false, reason: 'auth-in-url' }

  const back = `${u.origin}${u.pathname}${u.search}` // no fragment, by design
  const url = `${HUB_HAND_OFF_URL}?return=${encodeURIComponent(back)}`
  if (url.length > 2048 + HUB_HAND_OFF_URL.length) return { redirect: false, reason: 'too-long' }
  return { redirect: true, url }
}

/** Browser glue. Writes the marker FIRST, then navigates. Returns true if it navigated. */
export function maybeRedirectToHub(hasSession: boolean, hadNonce: boolean): boolean {
  if (typeof window === 'undefined') return false
  let markerSet = false
  let markerWritable = true
  try {
    markerSet = window.sessionStorage.getItem(SSO_TRIED_KEY) !== null
    if (hadNonce && !markerSet) {
      // We came back from the hub (or someone handed us a nonce): this tab has had its attempt.
      window.sessionStorage.setItem(SSO_TRIED_KEY, '1')
      markerSet = true
    }
  } catch {
    markerWritable = false
  }
  let embedded = false
  try {
    embedded = window.self !== window.top
  } catch {
    embedded = true
  }
  const d = decideSsoRedirect({
    hasSession,
    href: window.location.href,
    userAgent: navigator.userAgent ?? '',
    webdriver: Boolean((navigator as Navigator & { webdriver?: boolean }).webdriver),
    embedded,
    markerSet,
    markerWritable,
  })
  if (!d.redirect) return false
  try {
    window.sessionStorage.setItem(SSO_TRIED_KEY, '1')
  } catch {
    return false
  }
  window.location.replace(d.url)
  return true
}
